import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  createBehaviorFactor,
  createKnowledgeNode,
  deleteKnowledgeNode,
  fetchBehaviorFactor,
  fetchBehaviorFactorsTree,
  fetchKnowledgeNode,
  fetchKnowledgeNodes,
  updateBehaviorFactor,
  updateKnowledgeNode,
} from './api';
import type {
  BehaviorFactorFormValues,
  BehaviorFactorsTreeParams,
  KnowledgeListParams,
  KnowledgeNodeCreate,
  KnowledgeNodeUpdate,
} from './types';

/**
 * Query keys for knowledge nodes
 */
export const knowledgeKeys = {
  all: [API_TAGS.KNOWLEDGE] as const,
  lists: () => [...knowledgeKeys.all, 'list'] as const,
  list: (params?: KnowledgeListParams) => [...knowledgeKeys.lists(), { ...params }] as const,
  details: () => [...knowledgeKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...knowledgeKeys.details(), Number(id)] as const,
};

/**
 * Hook to fetch a list of knowledge nodes.
 * `enabled` отключает список там, где знание берётся из другого источника (модель знаний).
 */
export const useKnowledgeNodes = (params?: KnowledgeListParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: knowledgeKeys.list(params),
    queryFn: () => fetchKnowledgeNodes(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to fetch a single knowledge node by ID
 */
export const useKnowledgeNode = (id?: number | string) => {
  return useQuery({
    // Ключ строится только по реальному идентификатору: `Number(undefined)` дал бы `NaN`,
    // а он сериализуется в `null` и свёл бы в кэше все вызовы хука без идентификатора
    queryKey: id ? knowledgeKeys.detail(id) : knowledgeKeys.details(),
    queryFn: () => fetchKnowledgeNode(id!),
    enabled: !!id && id !== 'create',
  });
};

/**
 * Hook to create a knowledge node
 */
export const useCreateKnowledgeNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: KnowledgeNodeCreate) => createKnowledgeNode(payload),
    onSuccess: () => {
      // Invalidate list queries to refetch with new data
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.lists() });
    },
  });
};

/**
 * Hook to update a knowledge node
 */
export const useUpdateKnowledgeNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: KnowledgeNodeUpdate }) =>
      updateKnowledgeNode(id, payload),
    onSuccess: (updatedNode) => {
      queryClient.setQueryData(knowledgeKeys.detail(updatedNode.id), updatedNode);
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.detail(updatedNode.id) });
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.lists() });
    },
  });
};

/**
 * Hook to delete a knowledge node
 */
export const useDeleteKnowledgeNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteKnowledgeNode(id),
    onSuccess: (_, deletedId) => {
      // Invalidate list queries to refetch with updated data
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.lists() });

      // Remove from detail cache
      queryClient.removeQueries({ queryKey: knowledgeKeys.detail(deletedId) });
    },
  });
};

/**
 * Query keys for behavior factors
 */
export const behaviorFactorKeys = {
  all: [API_TAGS.KNOWLEDGE, 'behavior-factors'] as const,
  tree: (params?: BehaviorFactorsTreeParams) => [...behaviorFactorKeys.all, 'tree', { ...params }] as const,
  details: () => [...behaviorFactorKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...behaviorFactorKeys.details(), Number(id)] as const,
};

/**
 * Hook to fetch behavior factors tree
 */
export const useBehaviorFactorsTree = (params?: BehaviorFactorsTreeParams) => {
  return useQuery({
    queryKey: behaviorFactorKeys.tree(params),
    queryFn: () => fetchBehaviorFactorsTree(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch a single behavior factor by ID
 */
export const useBehaviorFactor = (id?: number | string) => {
  return useQuery({
    queryKey: behaviorFactorKeys.detail(id!),
    queryFn: () => fetchBehaviorFactor(id!),
    enabled: !!id && id !== 'create',
  });
};

/**
 * Hook to create a behavior factor
 */
export const useCreateBehaviorFactor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BehaviorFactorFormValues) => createBehaviorFactor(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: behaviorFactorKeys.all });
    },
  });
};

/**
 * Hook to update a behavior factor
 */
export const useUpdateBehaviorFactor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: BehaviorFactorFormValues }) =>
      updateBehaviorFactor(id, payload),
    onSuccess: (updatedFactor) => {
      queryClient.setQueryData(behaviorFactorKeys.detail(updatedFactor.id), updatedFactor);
      queryClient.invalidateQueries({ queryKey: behaviorFactorKeys.tree() });
    },
  });
};

/**
 * Hook to delete a behavior factor (uses deleteKnowledgeNode)
 */
export const useDeleteBehaviorFactor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteKnowledgeNode(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: behaviorFactorKeys.all });
      queryClient.removeQueries({ queryKey: behaviorFactorKeys.detail(deletedId) });
    },
  });
};
